// The PE ORDERS columns (the workbook's PE ORDERS tab, Dijana 2026-10-30): the warehouse's own
// fields on a line, all named "PE Order …", hidden until a person shows them. A value the app can
// work out is a prefill — shown muted, never stored — until someone edits the cell; then the stored
// value wins. Empty (NULL or "") is "never edited": a save sends text as "", never null (the
// platform refuses a null text). PE Order Status emptied on purpose is stored as the marker
// PE_CLEARED, which no page shows, so no prefill comes back. 017-pe-orders.sql

import { addBusinessDays, asDay, formatDay, type Holidays } from "./days";
import { deposcoRank, isCanceledStatus } from "./deposco";
import { timeShown } from "./time";
import type { WarehouseField, WarehouseValues } from "./types";

export const PE_FIELDS = [
  "peShipDate",
  "pullGroup",
  "locQty",
  "peAssignedTo",
  "peEstimate",
  "peStatus",
  "peStartAt",
  "peEndAt",
  "pallets",
  "peComments",
] as const;
export type PeField = (typeof PE_FIELDS)[number];
export const isPeField = (f: string): f is PeField => (PE_FIELDS as readonly string[]).includes(f);

/** The Worklist's headers, the System log's words and the export's. */
export const PE_LABELS: Record<PeField, string> = {
  peShipDate: "PE Order Ship Date",
  pullGroup: "PE Order Pull Group #",
  locQty: "PE Order Loc Qty",
  peAssignedTo: "PE Order Assigned To",
  peEstimate: "PE Order Estimate",
  peStatus: "PE Order Status",
  peStartAt: "PE Order Start Time",
  peEndAt: "PE Order End Time",
  pallets: "PE Order Pallets",
  peComments: "PE Order Comments",
};

export const PE_STATUS_LIST = "PE Order Status";
export const PE_ASSIGNED_LIST = "PE Order Assigned to";
export const PE_IN_PROCESS = "IN PROCESS";
export const PE_COMPLETE = "COMPLETE";

export const PE_ASAP = "ASAP";
/**
 * PE Order Status emptied on purpose — stored as it is, the line's fields the same: no prefill any
 * more. Never shown (the cell is empty); no list value can be it (list values are upper case).
 */
export const PE_CLEARED = "(cleared)";

/** One prefilled or stored value as a cell shows it; `from` says where a prefill comes from. */
export interface PeCell {
  value: string;
  prefill: boolean;
  from: string;
}

const typed = (value: string): PeCell => ({ value, prefill: false, from: "" });
export const SHIP_DATE_RULE = "Ready date − 2 business days";

// ---------------------------------------------------------------------------------------------
// PE Order Ship Date: the warehouse's due date, READY DATE − 2 business days until one is picked.

/** READY DATE − 2 business days (weekends and `holidays` skipped); ASAP stays ASAP; null without one. */
export function peShipDatePrefill(readyDate: string, holidays: Holidays): string | null {
  if (readyDate.trim().toUpperCase() === PE_ASAP) return PE_ASAP;
  const day = asDay(readyDate);
  return day ? addBusinessDays(day, -2, holidays) : null;
}

/** The stored date or ASAP wins, always; cleared ("") the calculation is back. */
export function peShipDateCell(fields: { peShipDate: string; readyDate: string }, holidays: Holidays): PeCell {
  if (fields.peShipDate) return typed(fields.peShipDate);
  const pre = peShipDatePrefill(fields.readyDate, holidays);
  return pre ? { value: pre, prefill: true, from: SHIP_DATE_RULE } : typed("");
}

/** How the cell says it: "ASAP", "11/03/26". */
export const peShipDateText = (v: string) => (v === PE_ASAP ? PE_ASAP : asDay(v) ? formatDay(v) : v);
/** ASAP first, then by date, empty last. */
export const peShipDateSort = (v: string) => (v === PE_ASAP ? "0" : asDay(v) ? `1${v}` : "9");

// ---------------------------------------------------------------------------------------------
// PE Order Status: Deposco's from its SOs' highest status, A2000's COMPLETE once every pick ticket
// shipped in A2000; the list's values otherwise.

/** What a line's sources say its status is, or null. */
export function peStatusPrefill(line: {
  source: string;
  deposco: { status: { status: string } | null } | null;
  a2000: { all: boolean } | null;
}): { value: string; from: string } | null {
  if (line.source === "Deposco") {
    const status = line.deposco?.status?.status;
    if (!status) return null;
    const from = `Deposco status ${status}`;
    if (isCanceledStatus(status)) return { value: "CANCELLED", from: "Deposco: every SO Canceled" };
    const rank = deposcoRank(status);
    if (rank >= deposcoRank("Pick Complete")) return { value: PE_COMPLETE, from };
    if (rank === deposcoRank("Picking")) return { value: PE_IN_PROCESS, from };
    if ((["Released", "Back Ordered", "Wave Built"] as const).some((s) => rank === deposcoRank(s)))
      return { value: "PENDING", from };
    return null;
  }
  return line.a2000?.all ? { value: PE_COMPLETE, from: "Shipped in A2000 (every pick ticket)" } : null;
}

export function peStatusCell(stored: string, line: Parameters<typeof peStatusPrefill>[0]): PeCell {
  if (stored === PE_CLEARED) return typed("");
  if (stored) return typed(stored);
  const pre = peStatusPrefill(line);
  return pre ? { value: pre.value, prefill: true, from: pre.from } : typed("");
}

// ---------------------------------------------------------------------------------------------
// PE Order Start Time / End Time: set by a person's status change, when empty.

/** A local moment to the minute, as stored: "2026-10-30 14:05". */
export const momentAt = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")} ${String(
    d.getHours(),
  ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

const MOMENT = /^(\d{4}-\d{2}-\d{2}) (\d{2}):(\d{2})$/;
export const isMoment = (v: string) => MOMENT.test(v);
/** "10/30/26 2:05 PM": the day as every date, the time as `timeShown` says it. */
export const momentText = (v: string) => {
  const m = MOMENT.exec(v);
  return m ? `${formatDay(m[1]!)} ${timeShown(`${Number(m[2])}:${m[3]}`)}` : v;
};
/** A stored moment's day and time (H:MM, as the time picker reads it). */
export const momentParts = (v: string): { day: string; time: string } => {
  const m = MOMENT.exec(v);
  return m ? { day: m[1]!, time: `${Number(m[2])}:${m[3]}` } : { day: "", time: "" };
};
/** A day and a time (H:MM) as stored; "" when the day is missing. */
export const momentOf = (day: string, time: string) => {
  if (!day) return "";
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  return `${day} ${m ? `${m[1]!.padStart(2, "0")}:${m[2]}` : "00:00"}`;
};

/**
 * What a person's PE Order Status change sets by itself: IN PROCESS → Start Time now, COMPLETE →
 * End Time now, each only when empty. Saved with the status (one Undo); a prefill sets nothing.
 */
export function peStatusTimes(
  value: string,
  fields: { peStartAt: string; peEndAt: string },
  now: string,
): { peStartAt?: string; peEndAt?: string } {
  if (value === PE_IN_PROCESS && !fields.peStartAt) return { peStartAt: now };
  if (value === PE_COMPLETE && !fields.peEndAt) return { peEndAt: now };
  return {};
}

/** End before Start is refused, as Out before In. */
export function checkStartEnd(start: string, end: string): string | undefined {
  if (isMoment(start) && isMoment(end) && end < start)
    return `${PE_LABELS.peEndAt} (${momentText(end)}) can't be before ${PE_LABELS.peStartAt} (${momentText(start)})`;
  return undefined;
}

// ---------------------------------------------------------------------------------------------
// PE Order Estimate: minutes, typed "45", "45m", "1h 15m", "1:15"; shown "1h 15m".

export function parseEstimate(text: string): { ok: true; value: string } | { ok: false; message: string } {
  const t = text.trim().toLowerCase().replace(/\s+/g, " ");
  if (!t) return { ok: true, value: "" };
  const bad = { ok: false as const, message: "Use minutes or hours: 45, 45m, 1h 15m or 1:15" };
  let m = /^(\d+)\s*(m|min|mins|minutes?)?$/.exec(t);
  if (m) return { ok: true, value: String(Number(m[1])) };
  m = /^(\d+):([0-5]\d)$/.exec(t);
  if (m) return { ok: true, value: String(Number(m[1]) * 60 + Number(m[2])) };
  m = /^(\d+)\s*(?:h|hr|hrs|hours?)(?:\s*(\d+)\s*(?:m|min|mins|minutes?)?)?$/.exec(t);
  if (m) return { ok: true, value: String(Number(m[1]) * 60 + Number(m[2] ?? 0)) };
  return bad;
}

/** "1h 15m", "45m", "2h". */
export function estimateText(minutes: string): string {
  if (!/^\d+$/.test(minutes)) return minutes;
  const n = Number(minutes);
  const h = Math.floor(n / 60);
  const m = n % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}

// ---------------------------------------------------------------------------------------------
// Stored ↔ the line's fields (all text there).

/** The columns 017 adds to `routing_line`, as the store lists them. */
export interface PeStored {
  peShipDate: string | null;
  peShipAsap: number | boolean | null;
  pullGroup: string | null;
  locQty: string | null;
  peAssignedTo: string | null;
  peEstimate: number | null;
  peStatus: string | null;
  peStartAt: string | null;
  peEndAt: string | null;
  pallets: number | null;
  peComments: string | null;
}

const text = (v: unknown) => (v === null || v === undefined ? "" : String(v));
const count = (v: unknown) => (v === null || v === undefined || v === "" ? "" : String(Number(v)));

/** A stored line's PE fields, as text. */
export function peFields(r: Partial<PeStored>): Record<PeField, string> {
  const asap = r.peShipAsap === true || Number(r.peShipAsap) === 1;
  return {
    peShipDate: asap ? PE_ASAP : (asDay(r.peShipDate) ?? ""),
    pullGroup: text(r.pullGroup),
    locQty: text(r.locQty),
    peAssignedTo: text(r.peAssignedTo),
    peEstimate: count(r.peEstimate),
    peStatus: text(r.peStatus),
    peStartAt: text(r.peStartAt),
    peEndAt: text(r.peEndAt),
    pallets: count(r.pallets),
    peComments: text(r.peComments),
  };
}

/** The line's PE fields (any of them) as stored; a field not given is left out. */
export function peStored(f: Partial<Record<PeField, string>>): Partial<PeStored> {
  const out: Partial<PeStored> = {};
  const n = (v: string) => (v === "" ? null : Number(v));
  for (const [k, v] of Object.entries(f) as [PeField, string][]) {
    if (v === undefined) continue;
    if (k === "peShipDate") {
      out.peShipDate = v === PE_ASAP ? null : asDay(v);
      out.peShipAsap = v === PE_ASAP ? 1 : null;
    } else if (k === "peEstimate" || k === "pallets") out[k] = n(v);
    // Text as it is: "" is empty (never null — the platform refuses a null text).
    else out[k] = v;
  }
  return out;
}

/** A line's fields (any of them) as a `routing-line` record stores them: the PE ones through `peStored`. */
export function storedLineFields(
  fields: Partial<WarehouseValues>,
): Partial<Omit<WarehouseValues, PeField>> & Partial<PeStored> {
  const rest: Partial<Record<WarehouseField, string>> = {};
  const pe: Partial<Record<PeField, string>> = {};
  for (const [k, v] of Object.entries(fields) as [WarehouseField, string][]) {
    if (isPeField(k)) pe[k] = v;
    else rest[k] = v;
  }
  return { ...(rest as Partial<Omit<WarehouseValues, PeField>>), ...peStored(pe) };
}

/** A row's two prefilled PE cells, worked out with the rows (`toRows`). */
export interface PeView {
  shipDate: PeCell;
  status: PeCell;
}

export function peView(
  line: { fields: WarehouseValues } & Parameters<typeof peStatusPrefill>[0],
  holidays: Holidays,
): PeView {
  return {
    shipDate: peShipDateCell(line.fields, holidays),
    status: peStatusCell(line.fields.peStatus, line),
  };
}
